import { MaskService } from '@shared/services/mask.service';
import { catchError, map, of } from 'rxjs';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { environment } from 'src/environments/environment';
import { SelectPesquisavelService } from '@shared/components/select-pesquisavel/select-pesquisavel.service';
import {
  PesquisaCodigoTributacaoModalComponent,
  PesquisaCodigoTributacaoModalData,
  PesquisaCodigoTributacaoModalResult,
} from '@shared/components/pesquisa-codigo-tributacao-modal/pesquisa-codigo-tributacao-modal.component';
import { Component, Input, OnInit } from '@angular/core';

import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Empresa } from '../../../../../../../shared/services/models/usuario/empresa.model';
import { UsuarioLoginResponse } from '../../../../../../../shared/services/responses/usuario/usuario-login.response';
import { ModeloDocumento } from '../../../../../../../shared/services/models/dfe/modelo-documento.model';
import { AlertService, AlertType } from '../../../../../../../shared/services/alert.service';
import { ModeloDocumentoService } from '../../../../../../../shared/services/apis/dfe/modelo-documento.service';
import { AuthService } from '../../../../../../../shared/services/apis/usuario/auth.service';

@Component({
  selector: 'app-filtro-painel-nota-recebida',
  standalone: true,
  imports: [
    NgxMaskDirective,
    MatButtonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatIconModule,
    MatTooltipModule,
  ],
  providers: [provideNgxMask()],
  templateUrl: './filtro-painel-nota-recebida.component.html',
})
export class FiltroPainelNotaRecebidaComponent implements OnInit {
  readonly masks = MaskService.getMasks();
  public form: FormGroup;
  public listaEmpresas: Empresa[] = [];
  public listaModeloDocumento: ModeloDocumento[] = [];
  public isLoadingLookup: boolean = false;

  constructor(
    private selectPesquisavelService: SelectPesquisavelService,
    private dialog: MatDialog,

    private fb: FormBuilder,
    private authService: AuthService,
    private alertService: AlertService,
    private modeloDocumentoService: ModeloDocumentoService
  ) {
    this.form = this.fb.group({
      tiposNota: [[]],
      listIdEmpresa: [[]],
      pesquisar: [''],
      serie: [''],
      cfop: [''],
    });
  }

  @Input() isEdicao: boolean = false;

  ngOnInit(): void {
    this.carregarLookups();
    if (this.isEdicao) this.form.disable();
  }

  /**
   * Chamado pelo componente pai para popular o formulário na edição
   */
  @Input()
  set jsonFiltros(json: string) {
    if (json) {
      try {
        const valores = JSON.parse(json);
        this.form.patchValue(valores);
      } catch (e) {
        console.error('Erro ao fazer parse dos filtros', e);
      }
    }
  }

  /**
   * Retorna os valores atuais do formulário para o pai salvar
   */
  public getValues(): any {
    return this.form.getRawValue();
  }

  private carregarLookups(): void {
    this.isLoadingLookup = true;

    this.modeloDocumentoService.listar().subscribe({
      next: res => {
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription, AlertType.error);
          return;
        }
        this.listaModeloDocumento = res.modelos || [];
        this.carregarEmpresas(); // Carrega empresas após lookup
        this.isLoadingLookup = false;
      },
      error: err => {
        this.isLoadingLookup = false;
        console.error(err);
      },
    });
  }

  private carregarEmpresas(): void {
    const userInfo: UsuarioLoginResponse = this.authService.getUserInfos();

    // Lógica replicada do painel-nota-recebida.component.ts
    const empresaAtual = userInfo.listaEmpresa.find(x => x.idEmpresa == userInfo.idEmpresa);

    if (!userInfo.listaEmpresasDoGrupo?.length) {
      // Visão de empresa única
      if (empresaAtual) this.listaEmpresas.push(empresaAtual);
      // Opcional: Travar seleção se for único, ou deixar livre. Mantendo consistência com multi-select.
    } else {
      if (!userInfo.empresaPrincipalGrupo || userInfo.listaEmpresasDoGrupo.length === 1) {
        // Usuário sem permissão de grupo ou grupo de 1
        this.listaEmpresas = userInfo.listaEmpresasDoGrupo?.filter(x => x.idEmpresa == userInfo.idEmpresa);
      } else {
        // Visão multi-empresa
        this.listaEmpresas = userInfo.listaEmpresasDoGrupo;
      }
    }
  }

  public get selectedCompaniesText(): string {
    const selectedIds = this.form.get('listIdEmpresa')?.value as number[] | null;
    if (!selectedIds || selectedIds.length === 0) {
      return '';
    }
    if (selectedIds.length > 2) {
      return `${selectedIds.length} empresas selecionadas`;
    }
    return this.listaEmpresas
      .filter(empresa => selectedIds.includes(empresa.idEmpresa))
      .map(empresa => empresa.nomeEmpresa)
      .join(', ');
  }

  pesquisarCfop(): void {
      if (this.form.get('cfop')?.disabled) return;
      const ref = this.dialog.open(PesquisaCodigoTributacaoModalComponent, {
          width: '700px',
          maxWidth: '95vw',
          maxHeight: '85vh',
          panelClass: 'pesquisa-codigo-tributacao-dialog',
          data: {
              titulo: 'Pesquisar CFOP',
              buscarFn: (pesquisa: string) => this.selectPesquisavelService
                  .getSelectValues<{ codigo?: string; descricao: string }>(environment.urlApiMotorFiscal + 'Lookup/cfop', pesquisa)
                  .pipe(
                      map(list => ({
                          isError: false,
                          itens: list.map(i => ({ codigo: String(i.codigo ?? ''), descricao: i.descricao })),
                      })),
                      catchError(() => of({ isError: true, itens: [] })),
                  ),
              permitirManual: false,
              registrosIniciais: 20,
              valorInicial: this.form.get('cfop')?.value ?? undefined,
          } satisfies PesquisaCodigoTributacaoModalData,
      });
      ref.afterClosed().subscribe((result: PesquisaCodigoTributacaoModalResult | null) => {
          if (!result?.selecionado) return;
          this.form.get('cfop')?.setValue(result.selecionado.codigo.replace(/\D/g, ''));
          this.form.get('cfop')?.markAsDirty();
      });
  }
}
